
import React, { useEffect, useRef } from "react";
import { Modal, Form, Input, Button, Row, Col, message, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/AddAgent/actions";

const AddAgentForm = ({ visible, onCancel, editingRecord, totalAllAgentsData }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const { messages, error, buttonLoading } = useSelector(
    (state) => state?.AllAgentReducer
  );

  const getUniqueCreditAccounts = () => {
    const allCredits = totalAllAgentsData.map(agent => agent?.creditAccountNumber);
    return Array.from(new Set(allCredits)).filter(Boolean);
  };

  useEffect(() => {
    if (messages) {
      messageApi.success(messages);
      dispatch(actions.addAgentsSuccess(null));
      onCancel();
      form.resetFields();
    }
  }, [messages, messageApi, dispatch, form, onCancel]);

  useEffect(() => {
    if (error) {
      messageApi.error(error);
      dispatch(actions.addAgentsFailed(null));
    }
  }, [error, messageApi, dispatch]);

  const baseBalanceRef = useRef(0);
  //console.log('....//\n\n', baseBalanceRef)

  useEffect(() => {
    if (editingRecord) {
      const baseBalance =
      editingRecord.currentBalance !== undefined
        ? Number(editingRecord.currentBalance)
        : 0;

      baseBalanceRef.current = baseBalance;
      form.setFieldsValue({
        agentId: editingRecord.agentId,
        agentName: editingRecord.agentName,
        debitAccountNumber: editingRecord.debitAccountNumber,
        creditAccountNumber: editingRecord.creditAccountNumber,
        tellerBranch: editingRecord.tellerBranch,
        tellerID: editingRecord.tellerID,
        aiIdentifier: editingRecord.aiIdentifier,
        currentBalance: formatToTwoDecimals(baseBalance),
        addBalance: 0,
      });
    } else {
      baseBalanceRef.current = 0;
      form.resetFields();
    }
  }, [editingRecord, form]);

  const handleAddBalanceChange = (value) => {
  const addAmount = Number(value || 0);

  form.setFieldsValue({
    currentBalance: formatToTwoDecimals(baseBalanceRef.current + addAmount),
  });
};

const formatToTwoDecimals = (value) => {
  const num = Number(value);
  if (isNaN(num)) return 0;
  return Number(num.toFixed(2));
};


  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const allValues = form.getFieldsValue(true);

      if (editingRecord) {
        const updatePayload = {
          agentName: allValues.agentName,
          currentBalance: allValues.currentBalance ? Number(allValues.currentBalance) : 0,
          
        };
        dispatch(actions.updateAgentDetail(editingRecord._id, updatePayload));
      } else {
        const createPayload = {
          ...allValues,
          currentBalance: allValues.currentBalance ? Number(allValues.currentBalance) : 0,
        };
        dispatch(actions.addAgents(createPayload));
      }
    } catch (error) {
      console.error("Form validation error:", error);
      messageApi.error(error.message || "Please fix the form errors");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={editingRecord ? "Edit Agent" : "Add Agent"}
        visible={visible}
        onCancel={handleCancel}
        width={780}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            loading={buttonLoading}
            onClick={handleSubmit}
            style={{ backgroundColor: "#073763", borderColor: "#073763" }}
          >
            {editingRecord ? "Update" : "Submit"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="agentId"
                label="Agent ID"
                rules={[{ required: !editingRecord, message: "Please enter Agent ID" }]}
              >
                <Input placeholder="Enter Agent ID" disabled={!!editingRecord} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="agentName"
                label="Agent Name"
                rules={[{ required: true, message: "Please enter Agent Name" }]}
              >
                <Input placeholder="Enter Agent Name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="debitAccountNumber"
                label="Debit Account Number"
                rules={[{ required: !editingRecord, message: "Please enter Debit Account Number" }]}
              >
                <Input
                  placeholder="Enter Debit Account Number"
                  maxLength={16}
                  disabled={!!editingRecord}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="creditAccountNumber"
                label="Credit Account (Bank)"
                rules={[{ required: !editingRecord, message: "Please enter Credit Account Number" }]}
              >
                <Select
                  placeholder="Select or enter Credit Account Number"
                  showSearch
                  allowClear
                  disabled={!!editingRecord}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={getUniqueCreditAccounts().map((acct) => ({
                    label: acct,
                    value: acct,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="tellerBranch"
                label="Teller Branch"
              >
                <Input placeholder="Enter Teller Branch" disabled={!!editingRecord} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tellerID"
                label="Teller ID"
              >
                <Input placeholder="Enter Teller ID" disabled={!!editingRecord} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="aiIdentifier"
                label="AI Identifier"
                rules={[{ required: true, message: "Please enter AI Identifier" }]}
              >
                <Input placeholder="Enter AI Identifier" disabled={!!editingRecord} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="currentBalance"
                label="Current Balance"
                rules={[
                  { required: true, message: "Please enter current balance" },
                  {
                    type: "number",
                    message: "Current balance must be a valid number",
                    transform: (value) => (value ? Number(value) : 0),
                  },
                ]}
                normalize={(value) => {
                  if (value === "") return undefined;
                  return value ? Number(value) : 0;
                }}
              >
                <Input
                  type="number"
                  placeholder="Enter current balance"
                  step="0.01"
                  min="0"
                  onWheel={(e) => e.target.blur()}
                  onKeyPress={(e) => {
                    if (!/[0-9.]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  disabled={!!editingRecord}
                />
              </Form.Item>
              {editingRecord && (
              <Form.Item
                name="addBalance"
                label="Add Balance"
                rules={[
                  { required: true, message: "Please enter amount to add" },
                ]}
              >
                <Input
                  type="number"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  onChange={(e) => handleAddBalanceChange(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  onKeyPress={(e) => {
                    if (!/[0-9.]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AddAgentForm;

